import { paths } from "~/router/paths";
import { IPlan, SubscriptionType } from "~/types/billing";
import { type OnboardingIconName } from "./OnboardingIcon";

// DropshipTool's onboarding: the user picks the tools they want, sees a
// preview of each, and what the trial includes, before checkout.

export const ONBOARDING_FEATURES = [
  "product_library",
  "ad_spy",
  "store_tracker",
  "competitor_analysis",
  "sales_insights",
  "theme_detector",
] as const;

export type OnboardingFeature = (typeof ONBOARDING_FEATURES)[number];

// Each tool's icon and the page its quick start opens.
export const FEATURE_DETAILS: Record<
  OnboardingFeature,
  { icon: OnboardingIconName; path: string }
> = {
  product_library: { icon: "trending", path: paths.app.productLibrary },
  ad_spy: { icon: "megaphone", path: paths.app.adSpyTool },
  store_tracker: { icon: "store", path: paths.app.myTrackingStores },
  competitor_analysis: { icon: "target", path: paths.app.competitorAnalysis },
  sales_insights: { icon: "chart", path: paths.app.myTrackings },
  theme_detector: { icon: "palette", path: paths.app.themeDetector },
};

// Set when the user leaves for checkout and cleared once the welcome screen
// is dismissed. Holds the picked tools, so the welcome screen after payment
// can show their quick starts.
export const PICKED_FEATURES_KEY = "onboardingPickedFeatures";

export const toggleFeature = (
  selected: OnboardingFeature[],
  feature: OnboardingFeature
): OnboardingFeature[] =>
  selected.includes(feature)
    ? selected.filter((item) => item !== feature)
    : [...selected, feature];

// The picked tools in the Figma order, or every tool when none was picked
// (Skip on the first step).
export const orderedFeatures = (
  selected: readonly OnboardingFeature[]
): OnboardingFeature[] => {
  const picked = ONBOARDING_FEATURES.filter((feature) =>
    selected.includes(feature)
  );
  return picked.length > 0 ? picked : [...ONBOARDING_FEATURES];
};

export const readPickedFeatures = (): OnboardingFeature[] | null => {
  try {
    const stored = localStorage.getItem(PICKED_FEATURES_KEY);
    if (stored === null) return null;
    const features = JSON.parse(stored);
    return Array.isArray(features)
      ? ONBOARDING_FEATURES.filter((feature) => features.includes(feature))
      : [];
  } catch {
    return null;
  }
};

export type TrialLimit = {
  key: string;
  icon: OnboardingIconName;
  value: number;
  period: "day" | "week" | null;
  // The tool the limit belongs to, for the "Your pick" badge.
  feature: OnboardingFeature | null;
};

// What the Starter trial includes. Ad Spy credits and competitor research
// searches are read from the Starter plan's limits; the plans don't carry the
// others yet, so those show the design's values.
export const getTrialLimits = (plans: IPlan[] | undefined): TrialLimit[] => {
  const starter = plans?.find(
    (plan) =>
      plan.name === "Starter" && plan.interval === SubscriptionType.MONTHLY
  );
  const limits = starter?.limits;

  return [
    {
      key: "product_db_searches",
      icon: "search",
      value: 25,
      period: "day",
      feature: "product_library",
    },
    {
      key: "ad_spy_credits",
      icon: "megaphone",
      value: limits?.ad_spy_credits ?? 1000,
      period: null,
      feature: "ad_spy",
    },
    {
      key: "tracked_stores",
      icon: "store",
      value: 10,
      period: null,
      feature: "store_tracker",
    },
    {
      key: "tracked_products",
      icon: "package",
      value: 10,
      period: null,
      feature: "sales_insights",
    },
    {
      key: "competitor_research",
      icon: "target",
      value: limits?.competitor_research_searches ?? 10,
      period: "day",
      feature: "competitor_analysis",
    },
    {
      key: "portfolio_products",
      icon: "briefcase",
      value: 10,
      period: "week",
      feature: null,
    },
  ];
};
